import { concatLatestFrom } from '@ngrx/operators';
import {
  Action,
  createAction,
  createFeatureSelector,
  createReducer,
  createSelector,
  on,
  props,
  Store,
} from '@ngrx/store';
import { inject, Injectable, Injector } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  EMPTY,
  filter,
  from,
  map,
  mergeMap,
  Observable,
  of,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';
import { deleteToken, getToken, isSupported, Messaging } from '@angular/fire/messaging';
import Swal from 'sweetalert2';
import { BsModalService } from 'ngx-bootstrap/modal';
import { HttpParams } from '@angular/common/http';
import { environment } from '@environments/environment';
import { ApiService } from '@services/common/api.service';
import { authenticationActions, selectAuthentication } from '@redux/authentication.redux';
import { eventsActions } from '@redux/events.effects';
import { Database, orderByChild, query, ref, onValue } from '@angular/fire/database';
import type { Notification } from '@models/community/notification.model';
import { showIncomingNotificationToast } from '@notifications/functions/show-incoming-notification-toast';
import { resolveCommunityEventNotificationChrome } from '@notifications/functions/community-event-notification.functions';

export interface NotificationsState {
  isSupported: boolean;
  permissionGranted: boolean;
  permissionRequested: boolean;
  firebaseToken: string | null;
  //
  loaded: boolean;
  unreadTotal: number;
  total: number;
  //
  notifications: Notification[];
  unread: Notification[];
  incoming: Notification[];
}

export const initialNotificationsState: NotificationsState = {
  isSupported: false,
  permissionGranted: false,
  permissionRequested: false,
  firebaseToken: null,
  //
  loaded: false,
  unreadTotal: 0,
  total: 0,

  //
  notifications: [],
  unread: [],
  incoming: [],
};

export const notificationsStateKey = 'NotificationsState';

export const notificationsActions = {
  isSupported: createAction(`[${notificationsStateKey}]IsSupported`),
  isSupportedSuccess: createAction(
    `[${notificationsStateKey}]IsSupportedSuccess`,
    props<{ supported: boolean }>(),
  ),
  permissionGranted: createAction(
    `[${notificationsStateKey}]PermissionGranted`,
    props<{ permissionGranted: boolean }>(),
  ),
  permissionRequested: createAction(`[${notificationsStateKey}]PermissionRequested`),
  firebaseToken: createAction(
    `[${notificationsStateKey}]FirebaseToken`,
    props<{ firebaseToken: string | null }>(),
  ),
  //
  snapshot: createAction(
    `[${notificationsStateKey}]Snapshot`,
    props<{ snapshot: Notification[] }>(),
  ),
  notifications: createAction(`[${notificationsStateKey}]Notifications`),
  notificationsSuccess: createAction(
    `[${notificationsStateKey}]NotificationsSuccess`,
    props<{
      notifications: Notification[];
      incoming: Notification[];
    }>(),
  ),
  //
  asRead: createAction(`[${notificationsStateKey}]AsRead`, props<{ model: Notification }>()),
};

const selectNotificationsFeature = createFeatureSelector<NotificationsState>(notificationsStateKey);

export const selectNotifications = {
  isSupported: createSelector(
    selectNotificationsFeature,
    (state: NotificationsState) => state.isSupported,
  ),
  permissionGranted: createSelector(
    selectNotificationsFeature,
    (state: NotificationsState) => state.permissionGranted,
  ),
  permissionRequested: createSelector(
    selectNotificationsFeature,
    (state: NotificationsState) => state.permissionRequested,
  ),
  //
  loaded: createSelector(selectNotificationsFeature, (state: NotificationsState) => state.loaded),
  total: createSelector(selectNotificationsFeature, (state: NotificationsState) => state.total),
  unreadTotal: createSelector(
    selectNotificationsFeature,
    (state: NotificationsState) => state.unreadTotal,
  ),
  //
  notifications: createSelector(
    selectNotificationsFeature,
    (state: NotificationsState) => state.notifications,
  ),
  unread: createSelector(selectNotificationsFeature, (state: NotificationsState) => state.unread),
  incoming: createSelector(
    selectNotificationsFeature,
    (state: NotificationsState) => state.incoming,
  ),
  firebaseToken: createSelector(
    selectNotificationsFeature,
    (state: NotificationsState) => state.firebaseToken,
  ),
};

export const notificationsReducer = createReducer(
  initialNotificationsState,
  on(
    notificationsActions.isSupportedSuccess,
    (state, { supported }): NotificationsState => ({
      ...state,
      isSupported: supported,
    }),
  ),
  on(
    notificationsActions.permissionGranted,
    (state, { permissionGranted }): NotificationsState => ({
      ...state,
      permissionGranted,
      permissionRequested: true,
    }),
  ),
  on(
    notificationsActions.firebaseToken,
    (state, { firebaseToken }): NotificationsState => ({
      ...state,
      firebaseToken,
    }),
  ),
  //

  on(
    notificationsActions.notificationsSuccess,
    (state, { notifications, incoming }): NotificationsState => {
      return {
        ...state,
        notifications,
        incoming,
        unread: notifications.filter((x) => !x.readInd),
        loaded: true,
        total: notifications.length,
        unreadTotal: notifications.filter((x) => !x.readInd).length,
      };
    },
  ),
);

@Injectable()
export class NotificationsEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private injector = inject(Injector);
  private api = inject(ApiService);
  private database = inject(Database);
  private modal = inject(BsModalService);

  isSupported$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(notificationsActions.isSupported),
      mergeMap(() => {
        return from(isSupported()).pipe(
          map((supported) => {
            //            console.log('supported', supported);
            return notificationsActions.isSupportedSuccess({ supported: supported as boolean });
          }),
          catchError((error) => {
            console.error('Error verificando soporte de Firebase Messaging:', error);
            return of(notificationsActions.isSupportedSuccess({ supported: false }));
          }),
        );
      }),
    );
  });

  requestPermission$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(notificationsActions.isSupportedSuccess),
      filter(({ supported }) => supported),
      concatLatestFrom(() => this.store.select(selectNotifications.permissionRequested)),
      filter(([, permissionRequested]) => !permissionRequested),
      mergeMap(() =>
        from(Notification.requestPermission()).pipe(
          map((permission) => {
            if (permission === 'granted') {
              return notificationsActions.permissionGranted({ permissionGranted: true });
            } else {
              this.requestPermission();
              return notificationsActions.permissionGranted({ permissionGranted: false });
            }
          }),
          catchError((error) => {
            console.error('Error al solicitar permiso de notificación:', error);
            return of(notificationsActions.permissionGranted({ permissionGranted: false }));
          }),
        ),
      ),
    );
  });

  permissionGranted$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(notificationsActions.permissionGranted),
      concatLatestFrom(() => [
        this.store.select(selectAuthentication.selectIsAuthenticated),
        this.store.select(selectAuthentication.selectUser),
      ]),
      filter(
        ([{ permissionGranted }, isAuthenticated, user]) =>
          permissionGranted && isAuthenticated && !!user,
      ),
      switchMap(([, , user]) => {
        const messaging = this.injector.get(Messaging);

        return from(this.firebaseMessagingToken(messaging)).pipe(
          mergeMap((firebaseToken) => {
            if (firebaseToken) {
              localStorage.removeItem('hash');
              return this.api
                .post(`devices`, { deviceToken: firebaseToken, userId: user!.id })
                .pipe(
                  map(() => notificationsActions.firebaseToken({ firebaseToken })),
                  catchError(() => of(notificationsActions.firebaseToken({ firebaseToken }))),
                );
            }
            return of(notificationsActions.firebaseToken({ firebaseToken }));
          }),
          catchError(() => {
            return of(notificationsActions.firebaseToken({ firebaseToken: null }));
          }),
        );
      }),
    );
  });

  logoutNotifications$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(authenticationActions.logout),
        concatLatestFrom(() => [
          this.store.select(selectNotifications.isSupported),
          this.store.select(selectNotifications.permissionGranted),
          this.store.select(selectNotifications.firebaseToken),
          this.store.select(selectAuthentication.selectUser),
        ]),
        filter(([, isSupported, permissionGranted]) => isSupported && permissionGranted),
        switchMap(([, , , storedToken, user]) => {
          const messaging = this.injector.get(Messaging);
          const token$ = (
            storedToken ? of(storedToken) : from(this.firebaseMessagingToken(messaging))
          ).pipe(
            catchError((error) => {
              console.error('Error resolving Firebase token on logout:', error);
              return of(null as string | null);
            }),
          );

          return token$.pipe(
            switchMap((firebaseToken) => {
              localStorage.removeItem('hash');
              const clearLocal$ = from(deleteToken(messaging)).pipe(
                map(() => EMPTY),
                catchError((error) => {
                  console.error('Error removing Firebase token:', error);
                  return EMPTY;
                }),
              );

              if (!firebaseToken || !user?.id) {
                return clearLocal$;
              }

              return this.api
                .delete(`devices`, new HttpParams(), {
                  deviceToken: firebaseToken,
                  userId: user.id,
                })
                .pipe(
                  catchError(() => of(null)),
                  switchMap(() => clearLocal$),
                );
            }),
          );
        }),
      );
    },
    { dispatch: false },
  );

  notifications$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(notificationsActions.notifications),
      concatLatestFrom(() => [
        this.store.select(selectAuthentication.selectIsAuthenticated),
        this.store.select(selectAuthentication.selectUser),
      ]),
      filter(([, isAuthenticated, user]) => isAuthenticated && !!user),
      switchMap(([, , user]) => {
        const path = `${environment.notifications}/${user!.id}`;
        const reference = ref(this.database, path);
        const orderQuery = query(reference, orderByChild('timestamp'));

        return new Observable<Action>((observer) => {
          const unsubscribe = onValue(
            orderQuery,
            (snapshot) => {
              const notifications: Notification[] = [];
              snapshot.forEach((child) => {
                const value = child.val() as Notification | null;
                if (value) {
                  notifications.push(value);
                }
              });
              observer.next(notificationsActions.snapshot({ snapshot: notifications.reverse() }));
            },
            {
              onlyOnce: false,
            },
          );

          return () => unsubscribe();
        }).pipe(
          takeUntil(this.actions$.pipe(ofType(authenticationActions.logout))),
          catchError((error) => {
            console.error('Error al obtener notificaciones en tiempo real:', error);
            return EMPTY;
          }),
        );
      }),
    );
  });

  snapshot$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(notificationsActions.snapshot),
      concatLatestFrom(() => [
        this.store.select(selectAuthentication.selectIsAuthenticated),
        this.store.select(selectNotifications.loaded),
        this.store.select(selectNotifications.notifications),
        this.store.select(selectAuthentication.selectUser),
      ]),
      filter(([, isAuthenticated, , , user]) => isAuthenticated && !!user),
      switchMap(([{ snapshot }, , loaded, collection]) => {
        const notified = collection.map((c: Notification) => c.id); // Usa map directamente
        const incoming: Notification[] = loaded
          ? snapshot.filter((x) => !notified.includes(x.id))
          : [];

        return of(
          notificationsActions.notificationsSuccess({
            notifications: snapshot,
            incoming,
          }),
        );
      }),
    );
  });

  notificationsSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(notificationsActions.notificationsSuccess),
        concatLatestFrom(() => [
          this.store.select(selectAuthentication.selectIsAuthenticated),
          this.store.select(selectNotifications.incoming),
        ]),
        filter(([, isAuthenticated, incoming]) => isAuthenticated && incoming.length > 0),
        map(([, , incoming]) => {
          incoming.forEach((notification) => {
            const chrome = resolveCommunityEventNotificationChrome(notification);
            const eventId = notification.model?.id;
            showIncomingNotificationToast(notification, {
              onSeeDetails:
                chrome.canSeeDetails && eventId
                  ? () => this.openEventDetailsFromToast(notification, eventId)
                  : undefined,
            });
          });
        }),
      );
    },
    { dispatch: false },
  );

  private openEventDetailsFromToast(notification: Notification, eventId: string): void {
    if (!notification.readInd) {
      this.store.dispatch(notificationsActions.asRead({ model: notification }));
    }
    this.store.dispatch(eventsActions.details({ id: eventId }));
    this.actions$
      .pipe(
        ofType(eventsActions.detailsSuccess),
        filter(({ event }) => !!event && (event.eventId === eventId || event.id === eventId)),
        take(1),
      )
      .subscribe(({ event }) => {
        void import(
          '@calendar/components/calendar-event-details/calendar-event-details.component'
        ).then(({ CalendarEventDetailsComponent }) => {
          this.modal.show(CalendarEventDetailsComponent, {
            initialState: { data: event },
            backdrop: 'static',
            class: 'modal-dialog-centered',
          });
        });
      });
  }

  asRead$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(notificationsActions.asRead),
        concatLatestFrom(() => [this.store.select(selectAuthentication.selectIsAuthenticated)]),
        filter(([, isAuthenticated]) => isAuthenticated),

        switchMap(([{ model }]) => {
          return this.api
            .put<Notification>(`users/notifications/${model.id}`, {
              readInd: 1,
            })
            .pipe(
              map(() => {
                return of();
              }),
              catchError(() => {
                return of();
              }),
            );
        }),
      );
    },
    { dispatch: false },
  );

  requestPermission() {
    Swal.fire({
      title: 'Notification Permissions Required',
      html: `
        <p>You need to enable notifications in your browser settings.</p>
        <p>Follow these steps:</p>
        <ol class="text-start">
          <li>Click on the lock icon <i class="bx bx bx-slider-alt font-size-20"></i> in the address bar.</li>
          <li>Select <strong>Site settings</strong>.</li>
          <li>In the Permissions section, find <strong>Notifications</strong>.</li>
          <li>Set the notifications to <strong>Allow</strong>.</li>
          <li>Reload the page after making these changes.</li>
        </ol>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#0a58ca',
      cancelButtonColor: '#c12323',
      confirmButtonText: 'Request Again',
      cancelButtonText: 'Close',
    }).then((result) => {
      if (result.isConfirmed) {
        this.store.dispatch(notificationsActions.isSupported());
      }
    });
  }

  private async firebaseMessagingToken(messaging: Messaging): Promise<string | null> {
    try {
      if (!('serviceWorker' in navigator)) {
        return null;
      }
      const registration = await navigator.serviceWorker.ready;
      if (registration.waiting) {
        registration.waiting.postMessage({ action: 'skipWaiting' });
      }
      return await getToken(messaging, {
        vapidKey: environment.firebaseConfig.vapidKey,
        serviceWorkerRegistration: registration,
      });
    } catch (error) {
      console.error('Error obteniendo el token FCM:', error);
      return null;
    }
  }
}
